import { useState, useRef, useCallback, useMemo } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink,
  PaginationNext, PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Info,
  ShieldCheck, BookOpen, Loader2, XCircle, Download, Eye, Pencil, Trash2, Plus, Undo2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { parseCptCsv, ParsedImport, ValidationIssue, CsvRow } from '@/lib/import/cptCsvParser';
import { ORTHOPEDIC_PROOF_MAPS } from '@/lib/proofmap/orthopedicRules';

type ImportStage = 'upload' | 'validate' | 'review' | 'importing' | 'complete';

const ROWS_PER_PAGE = 20;
const CPT_PATTERN = /^[A-Za-z0-9]{4,5}$/;

// Inline editable cell component
function EditableCell({ value, onSave, mono = false, placeholder = '' }: {
  value: string;
  onSave: (v: string) => void;
  mono?: boolean;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <span
        onClick={() => { setDraft(value); setEditing(true); }}
        className={cn(
          'cursor-pointer hover:bg-primary/10 rounded px-1 py-0.5 -mx-1 transition-colors inline-block min-w-[40px]',
          mono && 'font-mono text-xs',
          !value && 'text-muted-foreground italic'
        )}
        title="Click to edit"
      >
        {value || placeholder || '—'}
      </span>
    );
  }

  return (
    <Input
      autoFocus
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => { onSave(draft); setEditing(false); }}
      onKeyDown={e => {
        if (e.key === 'Enter') { onSave(draft); setEditing(false); }
        if (e.key === 'Escape') setEditing(false);
      }}
      className={cn('h-7 text-xs px-1.5', mono && 'font-mono')}
    />
  );
}

export default function BulkCptImportPage() {
  const { user } = useAuthContext();
  const fileRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<ImportStage>('upload');
  const [parsed, setParsed] = useState<ParsedImport | null>(null);
  const [editedRows, setEditedRows] = useState<CsvRow[]>([]);
  const [editedErrorRows, setEditedErrorRows] = useState<CsvRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ inserted: number; skipped: number; errors: string[] } | null>(null);
  const [page, setPage] = useState(1);
  const [errorPage, setErrorPage] = useState(1);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast({ title: 'Invalid file', description: 'Please upload a .csv file', variant: 'destructive' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const result = parseCptCsv(text);
      setParsed(result);
      setEditedRows([...result.rows]);
      setEditedErrorRows([...result.errorRows]);
      setPage(1);
      setErrorPage(1);
      setStage('validate');
    };
    reader.readAsText(file);
  }, []);

  // Row editing helpers
  const updateRow = useCallback((index: number, field: keyof CsvRow, value: string) => {
    setEditedRows(prev => {
      const next = [...prev];
      if (field === 'subset_cpts') {
        next[index] = { ...next[index], subset_cpts: value.split(',').map(s => s.trim()).filter(Boolean) };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  }, []);

  const updateErrorRow = useCallback((index: number, field: keyof CsvRow, value: string) => {
    setEditedErrorRows(prev => {
      const next = [...prev];
      if (field === 'subset_cpts') {
        next[index] = { ...next[index], subset_cpts: value.split(',').map(s => s.trim()).filter(Boolean) };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  }, []);

  const deleteRow = useCallback((index: number) => {
    setEditedRows(prev => prev.filter((_, i) => i !== index));
    toast({ title: 'Row removed', description: 'Row removed from import batch.' });
  }, []);

  const deleteErrorRow = useCallback((index: number) => {
    setEditedErrorRows(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Move a fixed error row to valid rows
  const rescueErrorRow = useCallback((index: number) => {
    const row = editedErrorRows[index];
    if (!CPT_PATTERN.test(row.primary_cpt)) {
      toast({ title: 'Still invalid', description: 'Fix the primary CPT code first.', variant: 'destructive' });
      return;
    }
    const badSubsets = row.subset_cpts.filter(s => !CPT_PATTERN.test(s));
    if (badSubsets.length > 0) {
      toast({ title: 'Still invalid', description: `Fix subset CPTs: ${badSubsets.join(', ')}`, variant: 'destructive' });
      return;
    }
    setEditedErrorRows(prev => prev.filter((_, i) => i !== index));
    setEditedRows(prev => [...prev, row]);
    toast({ title: 'Row rescued', description: `Line ${row.line} moved to valid rows.` });
  }, [editedErrorRows]);

  // Use edited rows for import
  const validRowCount = editedRows.length;

  const handleImport = useCallback(async () => {
    if (!parsed || validRowCount === 0 || !user) return;
    setStage('importing');
    setImporting(true);

    let inserted = 0;
    let skipped = 0;
    const errors: string[] = [];

    for (const row of editedRows) {
      const { data: existing } = await supabase
        .from('procedures')
        .select('id')
        .eq('cpt_code', row.primary_cpt)
        .maybeSingle();

      if (existing) {
        skipped++;
        continue;
      }

      const { error } = await supabase
        .from('procedures')
        .insert({
          cpt_code: row.primary_cpt,
          name: row.notes || `Imported CPT ${row.primary_cpt}`,
          specialty: 'Orthopedics',
          billable_type: 'BILLABLE',
          active: true,
        });

      if (error) {
        errors.push(`Line ${row.line}: ${error.message}`);
      } else {
        inserted++;
      }

      for (const subCpt of row.subset_cpts) {
        const { data: subExisting } = await supabase
          .from('procedures')
          .select('id')
          .eq('cpt_code', subCpt)
          .maybeSingle();

        if (!subExisting) {
          await supabase.from('procedures').insert({
            cpt_code: subCpt,
            name: `Imported CPT ${subCpt}`,
            specialty: 'Orthopedics',
            billable_type: 'BILLABLE',
            active: true,
          });
        }
      }
    }

    await supabase.from('audit_logs').insert({
      user_id: user.id,
      action: 'bulk_import' as any,
      resource_type: 'procedures',
      details: {
        total_rows: editedRows.length,
        error_rows: editedErrorRows.length,
        inserted,
        skipped,
        errors_count: errors.length,
        unique_codes: new Set([...editedRows.map(r => r.primary_cpt), ...editedRows.flatMap(r => r.subset_cpts)]).size,
        timestamp: new Date().toISOString(),
      },
      phi_accessed: false,
    });

    setImportResult({ inserted, skipped, errors });
    setImporting(false);
    setStage('complete');
    toast({ title: 'Import Complete', description: `${inserted} new procedures added, ${skipped} duplicates skipped.` });
  }, [parsed, editedRows, editedErrorRows, user, validRowCount]);

  const handleReset = () => {
    setParsed(null);
    setEditedRows([]);
    setEditedErrorRows([]);
    setStage('upload');
    setImportResult(null);
    setPage(1);
    setErrorPage(1);
    if (fileRef.current) fileRef.current.value = '';
  };

  const severityIcon = (s: ValidationIssue['severity']) => {
    if (s === 'error') return <XCircle className="w-3.5 h-3.5 text-destructive" />;
    if (s === 'warning') return <AlertTriangle className="w-3.5 h-3.5 text-warning" />;
    return <Info className="w-3.5 h-3.5 text-primary" />;
  };

  const allCodes = useMemo(() => {
    const s = new Set<string>();
    editedRows.forEach(r => { s.add(r.primary_cpt); r.subset_cpts.forEach(c => s.add(c)); });
    return s;
  }, [editedRows]);

  const proofMapCodes = useMemo(() => new Set(ORTHOPEDIC_PROOF_MAPS.map(pm => pm.cpt_code)), []);
  const proofMapCoverage = allCodes.size > 0
    ? Math.round(([...allCodes].filter(c => proofMapCodes.has(c)).length / allCodes.size) * 100)
    : 0;

  const errorCount = parsed ? parsed.issues.filter(i => i.severity === 'error').length : 0;

  // Pagination helpers
  const totalPages = Math.ceil(editedRows.length / ROWS_PER_PAGE) || 1;
  const pagedRows = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return editedRows.slice(start, start + ROWS_PER_PAGE);
  }, [editedRows, page]);

  const errorTotalPages = Math.ceil(editedErrorRows.length / ROWS_PER_PAGE) || 1;
  const pagedErrorRows = useMemo(() => {
    const start = (errorPage - 1) * ROWS_PER_PAGE;
    return editedErrorRows.slice(start, start + ROWS_PER_PAGE);
  }, [editedErrorRows, errorPage]);

  // Get the actual index in the full array for a paged item
  const getRowIndex = (pageNum: number, indexInPage: number) => (pageNum - 1) * ROWS_PER_PAGE + indexInPage;

  const renderPagination = (currentPage: number, total: number, setFn: (p: number) => void) => {
    if (total <= 1) return null;
    const pages = Array.from({ length: total }, (_, i) => i + 1);
    const visible = pages.filter(p => p === 1 || p === total || Math.abs(p - currentPage) <= 1);

    return (
      <Pagination className="mt-3">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => setFn(Math.max(1, currentPage - 1))}
              className={cn(currentPage === 1 && 'pointer-events-none opacity-50', 'cursor-pointer')}
            />
          </PaginationItem>
          {visible.map((p, i) => (
            <PaginationItem key={p}>
              {i > 0 && visible[i - 1] !== p - 1 && <span className="px-1 text-muted-foreground">…</span>}
              <PaginationLink
                isActive={p === currentPage}
                onClick={() => setFn(p)}
                className="cursor-pointer"
              >
                {p}
              </PaginationLink>
            </PaginationItem>
          ))}
          <PaginationItem>
            <PaginationNext
              onClick={() => setFn(Math.min(total, currentPage + 1))}
              className={cn(currentPage === total && 'pointer-events-none opacity-50', 'cursor-pointer')}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    );
  };

  return (
    <OpsLayout>
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Upload className="w-6 h-6 text-primary" />
            Bulk CPT & Subset Import
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upload CSV to populate the Master Orthopedic CPT catalog with intelligent subset bundles
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2">
          {(['upload', 'validate', 'review', 'complete'] as const).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors',
                stage === s || (stage === 'importing' && s === 'review')
                  ? 'border-primary bg-primary text-primary-foreground'
                  : (['upload', 'validate', 'review', 'importing', 'complete'].indexOf(stage) > ['upload', 'validate', 'review', 'complete'].indexOf(s))
                    ? 'border-primary/50 bg-primary/20 text-primary'
                    : 'border-muted text-muted-foreground'
              )}>
                {i + 1}
              </div>
              <span className="text-xs font-medium text-muted-foreground capitalize hidden sm:inline">{s}</span>
              {i < 3 && <div className="w-8 h-px bg-border hidden sm:block" />}
            </div>
          ))}
        </div>

        {/* STAGE: Upload */}
        {stage === 'upload' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                Upload CSV File
              </CardTitle>
              <CardDescription>
                Columns are detected flexibly — exact header names and order are not required
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
              >
                <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium">Click to select CSV file</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Supports .csv files with primary CPT, subset bundles, and payer scope
                </p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileSelect}
              />

              <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                <p className="text-xs font-semibold flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> Example CSV
                </p>
                <pre className="text-[11px] font-mono text-muted-foreground leading-relaxed">
{`primary_cpt,subset_cpts,payer_scope,notes
27447,"29875,29876,20610",all,"TKA standard bundle"
29827,"29826,29824,29828",commercial,"Shoulder RCR bundle"
29881,"29877,29876",workers_comp,"Knee scope meniscectomy"`}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STAGE: Validate */}
        {stage === 'validate' && parsed && (
          <div className="space-y-4">
            {/* Editing hint */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
              <Pencil className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Click any cell</strong> to edit values before importing.
                You can fix errors in skipped rows and move them to the valid set, or remove rows you don't need.
              </p>
            </div>

            {/* Partial import banner */}
            {editedErrorRows.length > 0 && editedRows.length > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-warning/10 border border-warning/30">
                <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Partial import available</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <strong>{editedRows.length}</strong> rows are valid and ready to import.{' '}
                    <strong>{editedErrorRows.length}</strong> rows have errors — edit them below or they'll be skipped.
                  </p>
                </div>
              </div>
            )}

            <Tabs defaultValue="summary">
              <TabsList>
                <TabsTrigger value="summary" className="gap-1.5">
                  <Eye className="w-3.5 h-3.5" /> Valid Rows ({editedRows.length})
                </TabsTrigger>
                {editedErrorRows.length > 0 && (
                  <TabsTrigger value="skipped" className="gap-1.5">
                    <XCircle className="w-3.5 h-3.5" /> Skipped ({editedErrorRows.length})
                  </TabsTrigger>
                )}
                <TabsTrigger value="issues" className="gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Issues ({parsed.issues.length})
                </TabsTrigger>
                <TabsTrigger value="proofmap" className="gap-1.5">
                  <BookOpen className="w-3.5 h-3.5" /> Proof Map
                </TabsTrigger>
              </TabsList>

              <TabsContent value="summary">
                <Card>
                  <CardContent className="p-6 space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                      <div className="text-center p-3 bg-muted/30 rounded-lg">
                        <p className="text-2xl font-bold">{editedRows.length}</p>
                        <p className="text-xs text-muted-foreground">Valid Rows</p>
                      </div>
                      <div className="text-center p-3 bg-muted/30 rounded-lg">
                        <p className="text-2xl font-bold">{allCodes.size}</p>
                        <p className="text-xs text-muted-foreground">Unique CPTs</p>
                      </div>
                      <div className="text-center p-3 bg-destructive/10 rounded-lg">
                        <p className="text-2xl font-bold text-destructive">{editedErrorRows.length}</p>
                        <p className="text-xs text-muted-foreground">Skipped</p>
                      </div>
                      <div className="text-center p-3 bg-muted/30 rounded-lg">
                        <p className="text-2xl font-bold">{parsed.issues.filter(i => i.severity === 'warning').length}</p>
                        <p className="text-xs text-muted-foreground">Warnings</p>
                      </div>
                      <div className="text-center p-3 bg-muted/30 rounded-lg">
                        <p className="text-2xl font-bold">{proofMapCoverage}%</p>
                        <p className="text-xs text-muted-foreground">Evidence Library</p>
                      </div>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Line</TableHead>
                          <TableHead className="w-24">Primary</TableHead>
                          <TableHead>Subset CPTs</TableHead>
                          <TableHead className="w-28">Payer Scope</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedRows.map((row, idx) => {
                          const realIdx = getRowIndex(page, idx);
                          return (
                            <TableRow key={`${row.line}-${realIdx}`}>
                              <TableCell className="text-muted-foreground text-xs">{row.line}</TableCell>
                              <TableCell>
                                <EditableCell
                                  value={row.primary_cpt}
                                  onSave={v => updateRow(realIdx, 'primary_cpt', v.trim())}
                                  mono
                                />
                              </TableCell>
                              <TableCell>
                                <EditableCell
                                  value={row.subset_cpts.join(', ')}
                                  onSave={v => updateRow(realIdx, 'subset_cpts', v)}
                                  mono
                                  placeholder="comma-separated"
                                />
                              </TableCell>
                              <TableCell>
                                <EditableCell
                                  value={row.payer_scope}
                                  onSave={v => updateRow(realIdx, 'payer_scope', v.trim().toLowerCase())}
                                />
                              </TableCell>
                              <TableCell className="max-w-[200px]">
                                <EditableCell
                                  value={row.notes}
                                  onSave={v => updateRow(realIdx, 'notes', v)}
                                  placeholder="add notes…"
                                />
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                  onClick={() => deleteRow(realIdx)}
                                  title="Remove row"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {renderPagination(page, totalPages, setPage)}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="skipped">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-destructive">
                      <XCircle className="w-4 h-4" /> Rows With Errors — Edit to Fix
                    </CardTitle>
                    <CardDescription>
                      Fix values inline, then click the <Undo2 className="w-3 h-3 inline" /> button to move them to the valid set.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Line</TableHead>
                          <TableHead className="w-24">Primary</TableHead>
                          <TableHead>Subset CPTs</TableHead>
                          <TableHead>Error</TableHead>
                          <TableHead className="w-20">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedErrorRows.map((row, idx) => {
                          const realIdx = getRowIndex(errorPage, idx);
                          const rowIssues = parsed.issues.filter(i => i.line === row.line && i.severity === 'error');
                          return (
                            <TableRow key={`err-${row.line}-${realIdx}`} className="bg-destructive/5">
                              <TableCell className="text-muted-foreground text-xs">{row.line}</TableCell>
                              <TableCell>
                                <EditableCell
                                  value={row.primary_cpt}
                                  onSave={v => updateErrorRow(realIdx, 'primary_cpt', v.trim())}
                                  mono
                                />
                              </TableCell>
                              <TableCell>
                                <EditableCell
                                  value={row.subset_cpts.join(', ')}
                                  onSave={v => updateErrorRow(realIdx, 'subset_cpts', v)}
                                  mono
                                />
                              </TableCell>
                              <TableCell>
                                {rowIssues.map((issue, j) => (
                                  <p key={j} className="text-xs text-destructive">{issue.message}</p>
                                ))}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-primary hover:text-primary"
                                    onClick={() => rescueErrorRow(realIdx)}
                                    title="Move to valid rows"
                                  >
                                    <Undo2 className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                    onClick={() => deleteErrorRow(realIdx)}
                                    title="Remove row"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {renderPagination(errorPage, errorTotalPages, setErrorPage)}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="issues">
                <Card>
                  <CardContent className="p-4 space-y-2">
                    {parsed.issues.length === 0 ? (
                      <div className="flex items-center gap-2 py-6 justify-center text-sm text-muted-foreground">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        All validation checks passed
                      </div>
                    ) : (
                      parsed.issues.map((issue, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm p-2 rounded bg-muted/20">
                          {severityIcon(issue.severity)}
                          <div>
                            <span className="font-medium">
                              {issue.line > 0 ? `Line ${issue.line}` : 'File'}
                              {issue.field !== 'file' && ` → ${issue.field}`}
                            </span>
                            <p className="text-xs text-muted-foreground">{issue.message}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="proofmap">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-primary" />
                      Clinical Evidence Library Coverage
                    </CardTitle>
                    <CardDescription>
                      CPTs linked to proof map rules will enable automated dictation evidence highlighting
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span>{proofMapCoverage}% of imported codes have evidence rules</span>
                        <span>{[...allCodes].filter(c => proofMapCodes.has(c)).length} / {allCodes.size}</span>
                      </div>
                      <Progress value={proofMapCoverage} className="h-2" />
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {[...allCodes].sort().map(code => (
                        <div
                          key={code}
                          className={cn(
                            'flex items-center gap-1.5 p-2 rounded border text-xs font-mono',
                            proofMapCodes.has(code)
                              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400'
                              : 'bg-muted/30 border-border text-muted-foreground'
                          )}
                        >
                          {proofMapCodes.has(code)
                            ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                            : <XCircle className="w-3 h-3 text-muted-foreground shrink-0" />
                          }
                          {code}
                        </div>
                      ))}
                    </div>

                    <Separator />
                    <p className="text-xs text-muted-foreground">
                      <strong>{ORTHOPEDIC_PROOF_MAPS.length}</strong> CPT codes currently in the Clinical Evidence Library.
                      Codes without rules can still be imported — proof map rules can be added later.
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <div className="flex gap-3">
              <Button variant="outline" onClick={handleReset}>Start Over</Button>
              {validRowCount > 0 ? (
                <Button onClick={() => setStage('review')} className="gap-1.5">
                  <CheckCircle2 className="w-4 h-4" />
                  Import {validRowCount} Valid Rows
                  {editedErrorRows.length > 0 && (
                    <span className="text-xs opacity-70">({editedErrorRows.length} skipped)</span>
                  )}
                </Button>
              ) : (
                <Button disabled className="gap-1.5">
                  <XCircle className="w-4 h-4" />
                  No Valid Rows to Import
                </Button>
              )}
            </div>
          </div>
        )}

        {/* STAGE: Review / Confirm */}
        {stage === 'review' && parsed && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Confirm Import
              </CardTitle>
              <CardDescription>
                This action will be logged in the Immutable Audit Ledger for HITRUST compliance
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valid bundles to import</span>
                  <span className="font-semibold">{editedRows.length}</span>
                </div>
                {editedErrorRows.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rows skipped (errors)</span>
                    <span className="font-semibold text-destructive">{editedErrorRows.length}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Unique CPT codes</span>
                  <span className="font-semibold">{allCodes.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Evidence Library coverage</span>
                  <span className="font-semibold">{proofMapCoverage}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Warnings</span>
                  <span className="font-semibold">{parsed.issues.filter(i => i.severity === 'warning').length}</span>
                </div>
              </div>

              <div className="border rounded-lg p-3 bg-warning/5 border-warning/30">
                <p className="text-xs flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                  <span>
                    This import will be recorded in the <strong>Immutable Audit Ledger</strong> with your user ID,
                    timestamp, row counts, and proof map coverage statistics. Duplicate CPT codes will be skipped.
                  </span>
                </p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStage('validate')}>Back</Button>
                <Button onClick={handleImport} className="gap-1.5" disabled={importing}>
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Execute Import
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STAGE: Importing */}
        {stage === 'importing' && (
          <Card>
            <CardContent className="p-12 text-center space-y-4">
              <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
              <p className="text-sm font-medium">Importing procedures & logging to audit ledger…</p>
              <p className="text-xs text-muted-foreground">Do not close this page</p>
            </CardContent>
          </Card>
        )}

        {/* STAGE: Complete */}
        {stage === 'complete' && importResult && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
                Import Complete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-emerald-500/10 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-600">{importResult.inserted}</p>
                  <p className="text-xs text-muted-foreground">Inserted</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold">{importResult.skipped}</p>
                  <p className="text-xs text-muted-foreground">Skipped (Duplicates)</p>
                </div>
                <div className="text-center p-4 bg-destructive/10 rounded-lg">
                  <p className="text-2xl font-bold text-destructive">{importResult.errors.length}</p>
                  <p className="text-xs text-muted-foreground">Errors</p>
                </div>
              </div>

              {editedErrorRows.length > 0 && (
                <div className="border rounded-lg p-3 bg-destructive/5 border-destructive/20">
                  <p className="text-xs font-medium text-destructive mb-1">
                    {editedErrorRows.length} rows were skipped due to validation errors:
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    {editedErrorRows.slice(0, 10).map(r => {
                      const issue = parsed?.issues.find(i => i.line === r.line && i.severity === 'error');
                      return (
                        <li key={r.line}>Line {r.line}: {issue?.message || 'Validation error'}</li>
                      );
                    })}
                    {editedErrorRows.length > 10 && (
                      <li>…and {editedErrorRows.length - 10} more</li>
                    )}
                  </ul>
                </div>
              )}

              {importResult.errors.length > 0 && (
                <div className="space-y-1">
                  {importResult.errors.map((err, i) => (
                    <p key={i} className="text-xs text-destructive">{err}</p>
                  ))}
                </div>
              )}

              <div className="border rounded-lg p-3 bg-primary/5 border-primary/30">
                <p className="text-xs flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    Audit entry recorded. View in <strong>Admin → Audit Log</strong> for full traceability.
                  </span>
                </p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={handleReset}>Import Another File</Button>
                <Button variant="outline" onClick={() => window.location.href = '/admin/procedure-catalog'} className="gap-1.5">
                  <Eye className="w-4 h-4" />
                  View Procedure Catalog
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </OpsLayout>
  );
}
